import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import { filtrarEntradasParaCliente, filtrarSalidasParaCliente } from './permisos';
import { 
  FileText, Plus, Edit, Trash2, Save, X, Eye, Search, Filter, 
  Calendar, DollarSign, User, Clock, ChevronDown, ChevronUp,
  Loader2, CheckCircle2, XCircle, AlertCircle, Info, Users,
  Building2, BarChart3, Download, TrendingUp, TrendingDown, Settings
} from 'lucide-react';

// ✅ CONFIGURACIÓN ANTI-ZOOM OBLIGATORIA
if (typeof document !== 'undefined') {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement('meta');
    metaViewport.name = 'viewport';
    document.head.appendChild(metaViewport);
  }
  metaViewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';
}

// ✅ COLORES DE VISTA FACTURAS-COMPROBANTES
const getVistaColor = () => ({
  gradient: 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)',
  solid: '#eab308',
  light: '#eab30810',
  description: 'Documentos fiscales'
});

// Utilidades mantenidas 100%
const formatDateForInput = (dateString) => {
  if (!dateString) return new Date().toISOString().split('T')[0];
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  return dateString;
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${day}/${month}/${year}`;
};

const formatMonthForSelect = (monthString) => {
  const [year, month] = monthString.split('-');
  const date = new Date(year, month - 1, 15);
  return new Intl.DateTimeFormat('es-ES', { 
    year: 'numeric', 
    month: 'long' 
  }).format(date);
};

// ✅ DROPDOWN PERSONALIZADO UNIFICADO
const CustomDropdown = ({ value, onChange, options, placeholder, disabled = false, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const selected = options.find(opt => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '14px 16px',
          border: '2px solid #f1f5f9',
          borderRadius: '12px',
          background: disabled ? '#f8fafc' : 'white',
          color: disabled ? '#94a3b8' : '#1e293b',
          fontSize: '16px',
          fontWeight: '500',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: disabled ? 'not-allowed' : 'pointer',
          outline: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          minHeight: '44px',
          touchAction: 'manipulation',
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)'
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#3b82f6';
          e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = '#f1f5f9';
          e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          {Icon && <Icon size={16} color="#64748b" />}
          <span style={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            fontSize: selectedOption?.style?.fontSize || '16px'
          }}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown 
          size={16} 
          style={{ 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            flexShrink: 0,
            color: '#64748b'
          }} 
        />
      </button>
      
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 1000,
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
          marginTop: '4px',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {options.map((option, index) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                background: option.value === value ? '#eff6ff' : 'transparent',
                color: option.value === value ? '#3b82f6' : '#374151',
                fontSize: option.style?.fontSize || '16px',
                fontWeight: option.value === value ? '600' : '400',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                touchAction: 'manipulation'
              }}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.target.style.background = '#f8fafc';
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.target.style.background = 'transparent';
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ✅ MODAL PARA VER DOCUMENTO CON DISEÑO UNIFICADO
function DocumentViewModal({ url, tipo, fileName, onClose, isMobile }) {
  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || `${tipo}_${Date.now()}.${url.split('.').pop()}`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const isImage = url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  const isPDF = url.match(/\.pdf$/i);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '10px',
      animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: isMobile ? '100%' : '90vw',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
        animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        {/* Header unificado */}
        <div style={{
          background: tipo === 'factura' ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
          color: 'white',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileText size={20} />
            <h3 style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: '600'
            }}>
              {tipo === 'factura' ? 'Factura' : 'Comprobante'}
            </h3>
          </div>
          
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleDownload}
              className="button-hover"
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 12px',
                color: 'white',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                touchAction: 'manipulation'
              }}
            >
              <Download size={14} />
              Descargar
            </button>
            <button
              onClick={onClose}
              className="button-hover"
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 12px',
                color: 'white',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                touchAction: 'manipulation'
              }}
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Contenido del documento */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#000',
          minHeight: isMobile ? '400px' : '500px'
        }}>
          {isImage ? (
            <img
              src={url}
              alt={tipo}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
            />
          ) : isPDF ? (
            <iframe
              src={url}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                minHeight: '500px'
              }}
              title={tipo}
            />
          ) : (
            <div style={{
              color: 'white',
              textAlign: 'center',
              padding: '40px'
            }}>
              <FileText size={64} color="white" style={{ marginBottom: '16px' }} />
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                Archivo no previsualizable
              </p>
              <button
                onClick={handleDownload}
                className="button-hover"
                style={{
                  background: tipo === 'factura' ? '#f59e0b' : '#22c55e',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  touchAction: 'manipulation'
                }}
              >
                Descargar archivo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FacturasComprobantes({ constructora, usuario, permisosCliente }) {
  const [activeTab, setActiveTab] = useState('facturas');
  const [documentos, setDocumentos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProject, setFilterProject] = useState(() => {
    // Intentar restaurar el último filtro de proyecto desde localStorage
    try {
      const saved = localStorage.getItem('facturasComprobantes_filterProject');
      return saved || 'todos';
    } catch (error) {
      console.error('Error restaurando filtro de proyecto:', error);
      return 'todos';
    }
  });
  const [filterMonth, setFilterMonth] = useState('todos');
  const [filterFacturador, setFilterFacturador] = useState('todos');
  const [filterFacturaANombre, setFilterFacturaANombre] = useState('todos');
  const [filterFuente, setFilterFuente] = useState('todas');
  
  // Estados para modal de documento
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  
  // Estado para filtros colapsables
  const [showFilters, setShowFilters] = useState(false);
  
  // Datos filtrados
  const [documentosFiltrados, setDocumentosFiltrados] = useState([]);
  const [cuitsFacturadores, setCuitsFacturadores] = useState([]);
  const [cuitsFacturaANombre, setCuitsFacturaANombre] = useState([]);

  const colorVista = getVistaColor();

  // Determinar permisos (mantenido 100%)
  const isCliente = usuario?.tipo_usuario === 'cliente';
  const isLlaveEnMano = isCliente && permisosCliente?.tipoProyecto === 'llave_en_mano';
  const isAdministracion = isCliente && permisosCliente?.tipoProyecto === 'administracion';
  const isConstructoraOrEmpleado = usuario?.tipo_usuario === 'constructora' || usuario?.tipo_usuario === 'empleado';

  // ✅ FUNCIONES DE DESCARGA
  const generateFileName = (doc) => {
    const fecha = formatDateForDisplay(doc.fecha).replace(/\//g, '-');
    const tipo = doc.tipo_documento === 'factura' ? 'factura' : 'comprobante';
    const codigo = doc.proyectos?.codigo || 'SIN-CODIGO';
    const extension = doc.documento_url?.split('.').pop() || 'pdf';
    return `${fecha}_${tipo}_${codigo}.${extension}`;
  };

  const downloadFile = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error descargando archivo:', error);
      alert('Error al descargar el archivo');
    }
  };

  const downloadAllAsZip = async () => {
    try {
      // Filtrar documentos que tienen archivos
      const documentosConArchivos = documentosFiltrados.filter(doc => doc.documento_url);
      
      if (documentosConArchivos.length === 0) {
        alert('No hay documentos para descargar');
        return;
      }

      // Crear ZIP usando JSZip
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Descargar cada archivo y agregarlo al ZIP
      for (const doc of documentosConArchivos) {
        try {
          const response = await fetch(doc.documento_url);
          const blob = await response.blob();
          const fileName = generateFileName(doc);
          zip.file(fileName, blob);
        } catch (error) {
          console.error(`Error descargando ${doc.documento_url}:`, error);
        }
      }

      // Generar y descargar el ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const downloadUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `documentos_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error creando ZIP:', error);
      alert('Error al crear el archivo ZIP');
    }
  };

  // ✅ SCROLL RESET OBLIGATORIO al cambiar vista
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ✅ ESCAPE handler obligatorio para cerrar modales
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowDocumentModal(false);
        setSelectedDocument(null);
        setSelectedDocumentType(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (constructora?.id) {
      cargarDatos();
    }
  }, [constructora]);

  // Efectos mantenidos 100%
  useEffect(() => {
    if (isCliente && permisosCliente?.proyectoId && filterProject === 'todos') {
      setFilterProject(permisosCliente.proyectoId);
      guardarFiltroProyecto(permisosCliente.proyectoId);
    }
  }, [permisosCliente, isCliente]);

  useEffect(() => {
    if (isLlaveEnMano && permisosCliente?.proyectoId && filterFacturaANombre === 'todos') {
      const proyecto = proyectos.find(p => p.id === permisosCliente.proyectoId);
      if (proyecto?.cuit_cliente) {
        setFilterFacturaANombre(proyecto.cuit_cliente);
      }
    }
  }, [isLlaveEnMano, permisosCliente, proyectos, filterFacturaANombre]);

  useEffect(() => {
    aplicarFiltros();
  }, [documentos, searchTerm, filterProject, filterMonth, filterFacturador, filterFacturaANombre, filterFuente, activeTab, permisosCliente]);

  useEffect(() => {
    actualizarCuitsDisponibles();
  }, [documentos, filterProject]);

  // Función para guardar el filtro de proyecto en localStorage
  const guardarFiltroProyecto = (filtro) => {
    try {
      localStorage.setItem('facturasComprobantes_filterProject', filtro);
    } catch (error) {
      console.error('Error guardando filtro de proyecto:', error);
    }
  };

  // Funciones mantenidas 100% (cargarDatos, aplicarFiltros, etc.)
  const cargarDatos = async () => {
    try {
      // Cargar proyectos según permisos
      let proyectosQuery = supabase
        .from('proyectos')
        .select('*')
        .eq('constructora_id', constructora.id)
        .eq('activo', true)
        .is('deleted_at', null);

      if (isCliente && permisosCliente?.proyectoId) {
        proyectosQuery = proyectosQuery.eq('id', permisosCliente.proyectoId);
      }

      const { data: proyectosData, error: proyectosError } = await proyectosQuery.order('codigo');
      
      if (proyectosError) {
        console.error('Error cargando proyectos:', proyectosError);
      } else {
        setProyectos(proyectosData || []);
      }

      // Cargar proveedores
      const { data: proveedoresData, error: proveedoresError } = await supabase
        .from('proveedores_servicios')
        .select('*')
        .eq('constructora_id', constructora.id)
        .order('nombre');
      
      if (proveedoresError) {
        console.error('Error cargando proveedores:', proveedoresError);
      } else {
        setProveedores(proveedoresData || []);
      }

      // Cargar trabajadores (para resolver nombres por CUIT)
      const { data: trabajadoresData, error: trabajadoresError } = await supabase
        .from('trabajadores')
        .select('id, nombre, apellido, cuit')
        .eq('constructora_id', constructora.id)
        .order('apellido');

      if (trabajadoresError) {
        console.error('Error cargando trabajadores:', trabajadoresError);
      } else {
        setTrabajadores(trabajadoresData || []);
      }

      // Cargar cajas
      const { data: cajasData, error: cajasError } = await supabase
        .from('cajas')
        .select('*')
        .eq('constructora_id', constructora.id)
        .order('nombre');
      
      if (cajasError) {
        console.error('Error cargando cajas:', cajasError);
      } else {
        setCajas(cajasData || []);
      }

      // Cargar entradas y salidas
      await cargarDocumentos();

    } catch (err) {
      console.error('Error cargando datos:', err);
    }
    setLoading(false);
  };

  const cargarDocumentos = async () => {
    const documentosCompletos = [];

    try {
      // Cargar ENTRADAS con facturas y comprobantes
      const { data: entradas, error: entradasError } = await supabase
        .from('entradas')
        .select(`
          *,
          proyectos (id, codigo, apellido_cliente, cuit_cliente),
          conceptos_entrada (nombre),
          trabajadores (nombre, apellido),
          cajas!entradas_caja_origen_id_fkey (id, nombre, tipo_caja)
        `)
        .eq('constructora_id', constructora.id)
        .eq('activo', true)
        .is('deleted_at', null)
        .or('factura_url.not.is.null,comprobante_url.not.is.null')
        .order('fecha', { ascending: false });

      if (entradasError) {
        console.error('Error cargando entradas:', entradasError);
      } else {
        entradas?.forEach(entrada => {
          // Agregar facturas de entradas (si tiene factura_url, independientemente del flag)
          if (entrada.factura_url) {
            documentosCompletos.push({
              ...entrada,
              fuente: 'entrada',
              tipo_documento: 'factura',
              documento_url: entrada.factura_url,
              concepto_nombre: entrada.conceptos_entrada?.nombre || 'Sin concepto',
              proveedor_nombre: entrada.trabajadores ? 
                `${entrada.trabajadores.nombre} ${entrada.trabajadores.apellido}` : 'Cliente directo'
            });
          }
          
          // Agregar comprobantes de entradas (solo si no es llave en mano y tiene comprobante_url)
          if (!isLlaveEnMano && entrada.comprobante_url) {
            documentosCompletos.push({
              ...entrada,
              fuente: 'entrada',
              tipo_documento: 'comprobante',
              documento_url: entrada.comprobante_url,
              concepto_nombre: entrada.conceptos_entrada?.nombre || 'Sin concepto',
              proveedor_nombre: entrada.trabajadores ? 
                `${entrada.trabajadores.nombre} ${entrada.trabajadores.apellido}` : 'Cliente directo'
            });
          }
        });
      }

      // Cargar SALIDAS con facturas y comprobantes
      const { data: salidas, error: salidasError } = await supabase
        .from('salidas')
        .select(`
          *,
          proyectos (id, codigo, apellido_cliente, cuit_cliente),
          conceptos_salida (nombre),
          proveedores_servicios (nombre, cuit),
          cajas!salidas_caja_origen_id_fkey (id, nombre, tipo_caja)
        `)
        .eq('constructora_id', constructora.id)
        .eq('activo', true)
        .is('deleted_at', null)
        .or('factura_url.not.is.null,comprobante_url.not.is.null')
        .order('fecha', { ascending: false });

      if (salidasError) {
        console.error('Error cargando salidas:', salidasError);
      } else {
        salidas?.forEach(salida => {
          // Agregar facturas de salidas (si tiene factura_url, independientemente del flag)
          if (salida.factura_url) {
            documentosCompletos.push({
              ...salida,
              fuente: 'salida',
              tipo_documento: 'factura',
              documento_url: salida.factura_url,
              concepto_nombre: salida.conceptos_salida?.nombre || 'Sin concepto',
              proveedor_nombre: salida.proveedores_servicios?.nombre || 'Sin proveedor'
            });
          }
          
          // Agregar comprobantes de salidas (solo si no es llave en mano y tiene comprobante_url)
          if (!isLlaveEnMano && salida.comprobante_url) {
            documentosCompletos.push({
              ...salida,
              fuente: 'salida',
              tipo_documento: 'comprobante',
              documento_url: salida.comprobante_url,
              concepto_nombre: salida.conceptos_salida?.nombre || 'Sin concepto',
              proveedor_nombre: salida.proveedores_servicios?.nombre || 'Sin proveedor'
            });
          }
        });
      }

      console.log('📋 Documentos cargados:', documentosCompletos.length);
      setDocumentos(documentosCompletos);

    } catch (err) {
      console.error('Error cargando documentos:', err);
    }
  };

  const aplicarFiltros = () => {
    let documentosParaProcesar = [...documentos];

    // 1. Filtrar por tipo de pestaña
    documentosParaProcesar = documentosParaProcesar.filter(doc => {
      if (activeTab === 'facturas') {
        return doc.tipo_documento === 'factura';
      } else {
        return doc.tipo_documento === 'comprobante';
      }
    });

    // 2. Aplicar filtros de permisos de cliente si corresponde
    if (isCliente && permisosCliente) {
      // Filtrar por proyecto del cliente
      documentosParaProcesar = documentosParaProcesar.filter(doc => 
        doc.proyecto_id === permisosCliente.proyectoId
      );

      // Para llave en mano: solo facturas a su nombre
      if (isLlaveEnMano && activeTab === 'facturas') {
        const cuitCliente = proyectos.find(p => p.id === permisosCliente.proyectoId)?.cuit_cliente;
        if (cuitCliente) {
          documentosParaProcesar = documentosParaProcesar.filter(doc => 
            doc.factura_a_nombre_de === cuitCliente
          );
        }
      }

      // Para administración: aplicar filtros de cajas (no mano de obra)
      if (isAdministracion) {
        if (activeTab === 'facturas') {
          // Filtrar entradas y salidas aplicando reglas de permisos
          const entradas = documentosParaProcesar.filter(d => d.fuente === 'entrada');
          const salidas = documentosParaProcesar.filter(d => d.fuente === 'salida');
          
          const entradasFiltradas = filtrarEntradasParaCliente(entradas, permisosCliente, cajas);
          const salidasFiltradas = filtrarSalidasParaCliente(salidas, permisosCliente, cajas);
          
          documentosParaProcesar = [...entradasFiltradas, ...salidasFiltradas];
        } else {
          // Misma lógica para comprobantes
          const entradas = documentosParaProcesar.filter(d => d.fuente === 'entrada');
          const salidas = documentosParaProcesar.filter(d => d.fuente === 'salida');
          
          const entradasFiltradas = filtrarEntradasParaCliente(entradas, permisosCliente, cajas);
          const salidasFiltradas = filtrarSalidasParaCliente(salidas, permisosCliente, cajas);
          
          documentosParaProcesar = [...entradasFiltradas, ...salidasFiltradas];
        }
      }
    }

    // 3. Filtros de interfaz
    documentosParaProcesar = documentosParaProcesar.filter(doc => {
      // Filtro de búsqueda
      const matchesSearch = doc.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           doc.proveedor_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           doc.concepto_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           doc.proyectos?.codigo?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtro por proyecto (solo para constructoras)
      const matchesProject = isCliente || filterProject === 'todos' || doc.proyecto_id === filterProject;
      
      // Filtro por mes
      const matchesMonth = filterMonth === 'todos' || 
                          doc.fecha.substring(0, 7) === filterMonth;
      
      // Filtro por facturador
      const matchesFacturador = filterFacturador === 'todos' || doc.facturador === filterFacturador;
      
      // Filtro por factura a nombre de
      const matchesFacturaANombre = filterFacturaANombre === 'todos' || doc.factura_a_nombre_de === filterFacturaANombre;
      
      // Filtro por fuente
      const matchesFuente = filterFuente === 'todas' || doc.fuente === filterFuente;
      
      return matchesSearch && matchesProject && matchesMonth && matchesFacturador && matchesFacturaANombre && matchesFuente;
    });

    // Ordenar por fecha descendente
    documentosParaProcesar.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

    setDocumentosFiltrados(documentosParaProcesar);
  };

  const actualizarCuitsDisponibles = () => {
    let documentosParaAnalizar = [...documentos];
    
    // Si hay un proyecto seleccionado, filtrar por ese proyecto
    if (filterProject !== 'todos') {
      documentosParaAnalizar = documentosParaAnalizar.filter(doc => doc.proyecto_id === filterProject);
    } else if (isCliente && permisosCliente?.proyectoId) {
      // Si es cliente, siempre filtrar por su proyecto
      documentosParaAnalizar = documentosParaAnalizar.filter(doc => doc.proyecto_id === permisosCliente.proyectoId);
    }

    // Obtener CUITs únicos de facturadores
    const facturadores = [...new Set(
      documentosParaAnalizar
        .filter(doc => doc.facturador)
        .map(doc => doc.facturador)
    )];

    // Obtener CUITs únicos de factura a nombre de
    const facturaANombre = [...new Set(
      documentosParaAnalizar
        .filter(doc => doc.factura_a_nombre_de)
        .map(doc => doc.factura_a_nombre_de)
    )];

    setCuitsFacturadores(facturadores);
    setCuitsFacturaANombre(facturaANombre);
  };

  const obtenerNombrePorCuit = (cuit) => {
    if (!cuit) return '';
    const normalize = (v) => (v || '').toString().replace(/[^0-9]/g, '');
    const target = normalize(cuit);

    // Constructora
    if (normalize(constructora?.cuit) === target) {
      return constructora.nombre;
    }

    // Proyectos (cliente)
    const proyecto = proyectos.find((p) => normalize(p.cuit_cliente) === target);
    if (proyecto) {
      return proyecto.apellido_cliente;
    }

    // Proveedores de servicios
    const proveedor = proveedores.find((p) => normalize(p.cuit) === target);
    if (proveedor) {
      return proveedor.nombre;
    }

    // Trabajadores
    const trabajador = trabajadores.find((t) => normalize(t.cuit) === target);
    if (trabajador) {
      return `${trabajador.apellido}, ${trabajador.nombre}`;
    }

    return cuit; // Fallback
  };

  const openDocumentModal = (documento) => {
    setSelectedDocument(documento.documento_url);
    setSelectedDocumentType(documento.tipo_documento);
    setShowDocumentModal(true);
  };

  // Calcular estadísticas para facturas
  const calcularEstadisticasFacturas = () => {
    // Considerar tanto SALIDAS como ENTRADAS con factura sobre el conjunto ya filtrado por UI
    const facturas = documentosFiltrados.filter(d => d.tipo_documento === 'factura');

    const toARS = (doc) => {
      const monto = parseFloat(doc.monto || 0);
      const tipoCambio = parseFloat(doc.tipo_cambio || 1);
      if (doc.moneda === 'USD') {
        const tc = Number.isFinite(tipoCambio) && tipoCambio > 0 ? tipoCambio : 1;
        return monto * tc;
      }
      return monto;
    };

    const montoA_ARS = facturas
      .filter(f => f.tipo_factura === 'A')
      .reduce((sum, f) => sum + toARS(f), 0);

    const montoBC_ARS = facturas
      .filter(f => f.tipo_factura === 'B' || f.tipo_factura === 'C')
      .reduce((sum, f) => sum + toARS(f), 0);

    const totalFacturado = facturas.reduce((sum, f) => sum + toARS(f), 0);

    const ivaDeducible = montoA_ARS * 0.21; // 21% de facturas A
    const gastoDeducibleGanancias = (montoA_ARS / 1.21) + montoBC_ARS; // A neto + B/C total
    const ahorroEstimadoGanancias = gastoDeducibleGanancias * 0.30; // 30%

    return {
      ivaDeducible,
      gastoDeducibleGanancias,
      ahorroEstimadoGanancias,
      totalFacturado,
    };
  };

  const stats = calcularEstadisticasFacturas();

  // Obtener meses únicos
  const mesesUnicos = [...new Set(documentosFiltrados.map(d => d.fecha.substring(0, 7)))].sort().reverse();

  // Verificar si el cliente llave en mano puede ver comprobantes (nunca)
  const puedeVerComprobantes = !isLlaveEnMano;

  // ✅ STATS DATA PARA RESUMEN COMPACTO
  const statsData = [
    {
      label: 'IVA deducible (A)',
      value: loading ? '...' : `$${Math.round(stats.ivaDeducible).toLocaleString()}`,
      icon: BarChart3,
      color: '#ef4444'
    },
    {
      label: 'Gasto deducible (Ganancias)',
      value: loading ? '...' : `$${Math.round(stats.gastoDeducibleGanancias).toLocaleString()}`,
      icon: DollarSign,
      color: '#10b981'
    },
    {
      label: 'Ahorro estimado ganancias (30%)',
      value: loading ? '...' : `$${Math.round(stats.ahorroEstimadoGanancias).toLocaleString()}`,
      icon: FileText,
      color: colorVista.solid
    },
    {
      label: 'Total facturado',
      value: loading ? '...' : `$${Math.round(stats.totalFacturado).toLocaleString()}`,
      icon: DollarSign,
      color: '#8b5cf6'
    }
  ];

  // ✅ PREPARAR OPCIONES PARA DROPDOWNS
  const proyectosOptions = [
    { value: 'todos', label: 'Todos los proyectos' },
    ...proyectos.map(proyecto => ({
      value: proyecto.id,
      label: `${proyecto.codigo} - ${proyecto.apellido_cliente}`,
      style: { fontSize: '12px' }
    }))
  ];

  const mesesOptions = [
    { value: 'todos', label: 'Todos los meses' },
    ...mesesUnicos.map(mes => ({
      value: mes,
      label: formatMonthForSelect(mes)
    }))
  ];

  const facturadoresOptions = [
    { value: 'todos', label: 'Todos los facturadores' },
    ...cuitsFacturadores.map(cuit => ({
      value: cuit,
      label: obtenerNombrePorCuit(cuit)
    }))
  ];

  const facturaANombreOptions = [
    { value: 'todos', label: 'Todas los facturados' },
    ...cuitsFacturaANombre.map((cuit) => ({
      value: cuit,
      label: obtenerNombrePorCuit(cuit)
    }))
  ];

  const fuentesOptions = [
    { value: 'todas', label: 'Todas las fuentes' },
    { value: 'entrada', label: 'Entradas' },
    { value: 'salida', label: 'Salidas' }
  ];

  

  return (
    <div className="main-container" style={{
      padding: isMobile ? '16px 12px 120px 12px' : '20px',
      maxWidth: '100%',
      margin: '0 auto',
      background: '#f8fafc',
      minHeight: '100vh',
      animation: 'fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      {/* ✅ HEADER UNIFICADO CON FILTROS */}
      <div className="header-section" style={{
        marginBottom: '20px',
        animation: 'slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '16px',
          minHeight: '56px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: '200px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px #2563eb40',
              flexShrink: 0
            }}>
              <FileText size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{
                fontSize: isMobile ? '20px' : '24px',
                fontWeight: '700',
                color: '#0f172a',
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                Facturas y Comprobantes
              </h1>
              <p style={{
                fontSize: '14px',
                color: '#64748b',
                margin: 0,
                fontWeight: '400',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {isLlaveEnMano 
                  ? `Facturas de tu proyecto ${permisosCliente?.proyectoCodigo || ''}`
                  : isCliente 
                    ? `Documentos de tu proyecto ${permisosCliente?.proyectoCodigo || ''}`
                    : 'Gestiona todos los documentos fiscales'
                }
              </p>
            </div>
          </div>
        </div>

        {/* ✅ PESTAÑAS UNIFICADAS */}
        <div style={{
          display: 'flex',
          marginBottom: '20px',
          background: 'white',
          borderRadius: '12px',
          padding: '4px',
          gap: '4px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          border: '1px solid #f1f5f9'
        }}>
          <button
            onClick={() => setActiveTab('facturas')}
            className="button-hover"
            style={{
              flex: 1,
              padding: '12px 20px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              background: activeTab === 'facturas' ? 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)' : 'transparent',
              color: activeTab === 'facturas' ? 'white' : '#64748b',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              touchAction: 'manipulation',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <FileText size={16} />
Facturas ({loading ? '...' : documentos.filter(d => d.tipo_documento === 'factura').length})
          </button>
          
          {puedeVerComprobantes && (
            <button
              onClick={() => setActiveTab('comprobantes')}
              className="button-hover"
              style={{
                flex: 1,
                padding: '12px 20px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                background: activeTab === 'comprobantes' ? 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)' : 'transparent',
                color: activeTab === 'comprobantes' ? 'white' : '#64748b',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                touchAction: 'manipulation',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <FileText size={16} />
Comprobantes ({loading ? '...' : documentos.filter(d => d.tipo_documento === 'comprobante').length})
            </button>
          )}
        </div>

        {/* ✅ FILTROS UNIFICADOS */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '20px'
        }}>
          {/* Búsqueda y botón de filtros */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            alignItems: 'center',
            gridColumn: isMobile ? '1' : '1 / -1' 
          }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={16} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#64748b',
                zIndex: 1
              }} />
              <input
                type="text"
                placeholder="Buscar por descripción, proveedor, concepto o proyecto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus={false}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '14px 16px 14px 44px',
                  border: '2px solid #f1f5f9',
                  borderRadius: '12px',
                  fontSize: '16px',
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  background: 'white',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                  outline: 'none',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxSizing: 'border-box',
                  touchAction: 'manipulation'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#f1f5f9';
                  e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)';
                }}
              />
            </div>
            
            {/* Botón para mostrar/ocultar filtros */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                background: showFilters ? colorVista.solid : 'white',
                color: showFilters ? 'white' : '#64748b',
                border: '2px solid #f1f5f9',
                borderRadius: '12px',
                padding: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                minWidth: '48px',
                minHeight: '48px'
              }}
              onMouseEnter={(e) => {
                if (!showFilters) {
                  e.target.style.borderColor = colorVista.solid;
                  e.target.style.color = colorVista.solid;
                }
              }}
              onMouseLeave={(e) => {
                if (!showFilters) {
                  e.target.style.borderColor = '#f1f5f9';
                  e.target.style.color = '#64748b';
                }
              }}
            >
              <Settings size={16} />
            </button>

            {/* Botón para descargar todo en ZIP */}
            <button
              onClick={downloadAllAsZip}
              style={{
                background: 'white',
                color: '#10b981',
                border: '2px solid #f1f5f9',
                borderRadius: '12px',
                padding: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                minWidth: '48px',
                minHeight: '48px'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#10b981';
                e.target.style.background = '#f0fdf4';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#f1f5f9';
                e.target.style.background = 'white';
              }}
              title="Descargar todos los documentos en ZIP"
            >
              <Download size={16} />
            </button>
          </div>

          {/* Filtros colapsables */}
          {showFilters && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
              gridColumn: isMobile ? '1' : '1 / -1',
              animation: 'slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              {/* Proyecto - Solo para constructoras */}
              {!isCliente && (
                <CustomDropdown
                  value={filterProject}
                  onChange={(value) => {
                    setFilterProject(value);
                    guardarFiltroProyecto(value);
                  }}
                  options={proyectosOptions}
                  placeholder="Proyecto"
                  icon={Building2}
                />
              )}

              {/* Mes */}
              <CustomDropdown
                value={filterMonth}
                onChange={setFilterMonth}
                options={mesesOptions}
                placeholder="Mes"
                icon={Calendar}
              />

              {/* Facturador - Solo para facturas */}
              {activeTab === 'facturas' && (
                <CustomDropdown
                  value={filterFacturador}
                  onChange={setFilterFacturador}
                  options={facturadoresOptions}
                  placeholder="Facturador"
                  icon={User}
                />
              )}

              {/* Factura a nombre de - Solo para facturas y no para llave en mano */}
              {activeTab === 'facturas' && !isLlaveEnMano && (
                <CustomDropdown
                  value={filterFacturaANombre}
                  onChange={setFilterFacturaANombre}
                  options={facturaANombreOptions}
                  placeholder="Factura a nombre de"
                  icon={User}
                />
              )}

              {/* Fuente - Solo para constructoras */}
              {!isCliente && (
                <CustomDropdown
                  value={filterFuente}
                  onChange={setFilterFuente}
                  options={fuentesOptions}
                  placeholder="Fuente"
                  icon={Filter}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* ✅ STATS COMPACTAS (SOLO DESKTOP) PARA FACTURAS */}
      {!isMobile && activeTab === 'facturas' && (
        <div className="stats-section" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '12px',
          marginBottom: '20px',
          animation: 'fadeInUp 0.7s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          {statsData.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div key={index} className="stat-card" style={{
                background: 'white',
                padding: '12px',
                borderRadius: '12px',
                textAlign: 'center',
                border: '1px solid #f1f5f9',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                minHeight: '80px',
                animation: `slideInRight 0.${8 + index}s cubic-bezier(0.4, 0, 0.2, 1)`
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: `${stat.color}10`,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 8px'
                }}>
                  <IconComponent size={16} color={stat.color} />
                </div>
                <p style={{
                  fontSize: '10px',
                  color: '#64748b',
                  margin: '0 0 4px 0',
                  fontWeight: '500',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {stat.label}
                </p>
                <p style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#1e293b',
                  margin: 0
                }}>
                  {stat.value}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* ✅ ESTADÍSTICAS MÓVILES PARA FACTURAS - SOLO MONTO Y IVA */}
      {isMobile && activeTab === 'facturas' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px',
          marginBottom: '20px',
          animation: 'fadeInUp 0.7s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          {stats.montoTotalARS > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
              padding: '16px',
              borderRadius: '12px',
              textAlign: 'center',
              border: '2px solid #22c55e'
            }}>
              <DollarSign size={20} style={{ marginBottom: '6px', color: '#166534' }} />
              <p style={{ fontSize: '11px', color: '#166534', margin: '0 0 4px 0', fontWeight: '600' }}>
                Total ARS
              </p>
              <p style={{ fontSize: '16px', fontWeight: '700', color: '#059669', margin: 0 }}>
                ${stats.montoTotalARS.toLocaleString()}
              </p>
            </div>
          )}

          {stats.montoTotalUSD > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, #f0f9ff, #dbeafe)',
              padding: '16px',
              borderRadius: '12px',
              textAlign: 'center',
              border: '2px solid #3b82f6'
            }}>
              <DollarSign size={20} style={{ marginBottom: '6px', color: '#1e40af' }} />
              <p style={{ fontSize: '11px', color: '#1e40af', margin: '0 0 4px 0', fontWeight: '600' }}>
                Total USD
              </p>
              <p style={{ fontSize: '16px', fontWeight: '700', color: '#2563eb', margin: 0 }}>
                US${stats.montoTotalUSD.toLocaleString()}
              </p>
            </div>
          )}

          {(stats.ivaARS + stats.ivaUSD) > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
              padding: '16px',
              borderRadius: '12px',
              textAlign: 'center',
              border: '2px solid #ef4444'
            }}>
              <BarChart3 size={20} style={{ marginBottom: '6px', color: '#991b1b' }} />
              <p style={{ fontSize: '11px', color: '#991b1b', margin: '0 0 4px 0', fontWeight: '600' }}>
                IVA Total
              </p>
              <p style={{ fontSize: '16px', fontWeight: '700', color: '#dc2626', margin: 0 }}>
                ${(stats.ivaARS + (stats.ivaUSD * 1000)).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ✅ CONTENIDO PRINCIPAL */}
      <div className="main-content" style={{
        animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
{loading ? (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "80px 20px",
      background: "white",
      borderRadius: "16px",
      border: "1px solid #f1f5f9",
    }}
  >
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          width: "32px",
          height: "32px",
          border: "2px solid #e2e8f0",
          borderTop: "2px solid #3b82f6",
          borderRadius: "50%",
          margin: "0 auto 16px",
          animation: "spin 1s linear infinite",
        }}
      />
      <p
        style={{
          margin: 0,
          fontSize: "14px",
          color: "#64748b",
          fontWeight: "500",
        }}
      >
        Cargando...
      </p>
    </div>
  </div>
) : documentosFiltrados.length === 0 ? (          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '40px 20px' : '60px 40px',
            textAlign: 'center',
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
            border: '2px dashed #cbd5e1'
          }}>
            <FileText size={48} color="#64748b" style={{ marginBottom: '20px' }} />
            <h3 style={{
              fontSize: isMobile ? '18px' : '20px',
              color: '#1e293b',
              marginBottom: '10px'
            }}>
              No se encontraron {activeTab}
            </h3>
            <p style={{
              color: '#64748b',
              fontSize: '15px',
              margin: 0
            }}>
              {isLlaveEnMano 
                ? 'No hay facturas registradas a tu nombre en este proyecto'
                : 'Intenta cambiar los filtros de búsqueda'
              }
            </p>
          </div>
        ) : isMobile ? (
          // ✅ CARDS MÓVILES ESTANDARIZADAS
          <div style={{ padding: '8px' }}>
            {documentosFiltrados.map((doc, index) => (
              <div 
                key={`${doc.fuente}-${doc.id}-${doc.tipo_documento}`}
                onClick={() => openDocumentModal(doc)}
                style={{
                  background: 'white',
                  border: '1px solid #f1f5f9',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: `slideInRight 0.${5 + (index % 10)}s cubic-bezier(0.4, 0, 0.2, 1)`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  touchAction: 'manipulation'
                }}
                onTouchStart={(e) => {
                  e.currentTarget.style.transform = 'scale(0.98)';
                  e.currentTarget.style.backgroundColor = '#f8fafc';
                }}
                onTouchEnd={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.backgroundColor = 'white';
                }}
              >
                {/* HEADER UNIFICADO */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#1e293b',
                      marginBottom: '4px'
                    }}>
                      {doc.proyectos?.codigo}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#64748b',
                      fontWeight: '500'
                    }}>
                      {doc.proveedor_nombre}
                    </div>
                  </div>
                  <div style={{
                    textAlign: 'right',
                    marginLeft: '12px'
                  }}>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '800',
                      color: activeTab === 'facturas' ? colorVista.solid : '#22c55e',
                      marginBottom: '2px'
                    }}>
                      {doc.moneda} ${Number(doc.monto).toLocaleString()}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: '4px'
                    }}>
                      <Calendar size={10} />
                      {formatDateForDisplay(doc.fecha)}
                    </div>
                  </div>
                </div>

                {/* BADGES UNIFICADOS */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '12px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{
                    background: activeTab === 'facturas' ? `${colorVista.solid}10` : '#f0fdf4',
                    color: activeTab === 'facturas' ? colorVista.solid : '#166534',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    {doc.concepto_nombre}
                  </span>
                  <span style={{
                    background: doc.fuente === 'entrada' ? '#dbeafe' : '#fef2f2',
                    color: doc.fuente === 'entrada' ? '#1e40af' : '#dc2626',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '9px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px'
                  }}>
                    {doc.fuente === 'entrada' ? <TrendingUp size={8} /> : <TrendingDown size={8} />}
                    {doc.fuente === 'entrada' ? 'ENTRADA' : 'SALIDA'}
                  </span>
                </div>

                {/* DESCRIPCIÓN (si existe) */}
                {doc.descripcion && (
                  <div style={{
                    fontSize: '12px',
                    color: '#64748b',
                    marginBottom: '12px',
                    lineHeight: '1.4'
                  }}>
                    {doc.descripcion}
                  </div>
                )}

                {/* DATOS DE FACTURACIÓN MÓVIL */}
                {activeTab === 'facturas' && (doc.facturador || doc.factura_a_nombre_de) && (
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    marginBottom: '12px',
                    background: '#f8fafc',
                    padding: '8px',
                    borderRadius: '6px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        {doc.facturador && (
                          <p style={{ margin: '0 0 2px 0' }}>
                            <strong>Facturador:</strong> {obtenerNombrePorCuit(doc.facturador)}
                          </p>
                        )}
                        {doc.factura_a_nombre_de && (
                          <p style={{ margin: 0 }}>
                            <strong>A nombre de:</strong> {obtenerNombrePorCuit(doc.factura_a_nombre_de)}
                          </p>
                        )}
                      </div>
                      {doc.tipo_factura && (
                        <span style={{
                          background: doc.tipo_factura === 'A' ? '#dcfce7' : '#fef3c7',
                          color: doc.tipo_factura === 'A' ? '#166534' : '#92400e',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: '600',
                          flexShrink: 0
                        }}>
                          {doc.tipo_factura}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* FOOTER UNIFICADO */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingTop: '12px',
                  borderTop: '1px solid #f1f5f9'
                }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openDocumentModal(doc);
                    }}
                    style={{
                      background: activeTab === 'facturas' ? `${colorVista.solid}20` : '#f0fdf4',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      color: activeTab === 'facturas' ? colorVista.solid : '#166534',
                      fontSize: '13px',
                      fontWeight: '600',
                      touchAction: 'manipulation',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Eye size={14} />
                    Ver {activeTab === 'facturas' ? 'Factura' : 'Comprobante'}
                  </button>
                </div>
              </div>
            ))}
          </div>
) : (
          // ✅ TABLA DESKTOP UNIFICADA
          <div style={{
            background: 'white',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
            border: '1px solid #f1f5f9'
          }}>
            <div style={{overflowX: 'auto'}}>
              <table style={{
  width: '100%',
  borderCollapse: 'collapse'
}}>
                <thead>
                  <tr style={{
                    background: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '14px'
                    }}>
                      Fecha
                    </th>
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '14px'
                    }}>
                      Proyecto
                    </th>
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '14px'
                    }}>
                      Proveedor/Cliente
                    </th>
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '14px'
                    }}>
                      Concepto
                    </th>
                    {activeTab === 'facturas' && (
                      <th style={{
                        padding: '16px 20px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '14px'
                      }}>
                        Facturación
                      </th>
                    )}
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '14px'
                    }}>
                      Fuente
                    </th>
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'right',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '14px'
                    }}>
                      Monto
                    </th>
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '14px',
                      width: '120px'
                    }}>
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {documentosFiltrados.map((doc, index) => (
                    <tr key={`${doc.fuente}-${doc.id}-${doc.tipo_documento}`} style={{
                      borderBottom: index < documentosFiltrados.length - 1 ? '1px solid #f1f5f9' : 'none',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.parentElement.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.target.parentElement.style.backgroundColor = 'transparent';
                    }}
                    >
                      <td style={{
                        padding: '16px 20px',
                        color: '#374151',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        {formatDateForDisplay(doc.fecha)}
                      </td>
                      <td style={{
                        padding: '16px 20px',
                        color: '#1e293b',
                        fontSize: '14px'
                      }}>
                        <div>
                          <div style={{ fontWeight: '600' }}>
                            {doc.proyectos?.codigo}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#64748b',
                            fontWeight: '400'
                          }}>
                            {doc.proyectos?.apellido_cliente}
                          </div>
                        </div>
                      </td>
                      <td style={{
                        padding: '16px 20px',
                        color: '#64748b',
                        fontSize: '14px'
                      }}>
                        <div>
                          <div style={{ fontWeight: '500' }}>{doc.proveedor_nombre}</div>
                          {doc.descripcion && (
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#94a3b8',
                              maxWidth: '150px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }} title={doc.descripcion}>
                              {doc.descripcion}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{
                        padding: '16px 20px',
                        color: '#64748b',
                        fontSize: '14px'
                      }}>
                        <span style={{
                          background: activeTab === 'facturas' ? `${colorVista.solid}20` : '#f0fdf4',
                          color: activeTab === 'facturas' ? colorVista.solid : '#166534',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '500'
                        }}>
                          {doc.concepto_nombre}
                        </span>
                      </td>
                      {activeTab === 'facturas' && (
                        <td style={{ 
                          padding: '16px 20px', 
                          color: '#64748b', 
                          fontSize: '12px',
                          maxWidth: '180px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div>
                              {doc.facturador && (
                                <div style={{ marginBottom: '4px' }}>
                                  <strong>De:</strong> {obtenerNombrePorCuit(doc.facturador)}
                                </div>
                              )}
                              {doc.factura_a_nombre_de && (
                                <div>
                                  <strong>Para:</strong> {obtenerNombrePorCuit(doc.factura_a_nombre_de)}
                                </div>
                              )}
                            </div>
                            {doc.tipo_factura && (
                              <span style={{
                                background: doc.tipo_factura === 'A' ? '#dcfce7' : '#fef3c7',
                                color: doc.tipo_factura === 'A' ? '#166534' : '#92400e',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '600',
                                flexShrink: 0
                              }}>
                                {doc.tipo_factura}
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                      <td style={{
                        padding: '16px 20px',
                        color: '#64748b',
                        fontSize: '12px'
                      }}>
                        <span style={{
                          background: doc.fuente === 'entrada' ? '#dbeafe' : '#fef2f2',
                          color: doc.fuente === 'entrada' ? '#1e40af' : '#dc2626',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          width: 'fit-content'
                        }}>
                          {doc.fuente === 'entrada' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          {doc.fuente === 'entrada' ? 'ENTRADA' : 'SALIDA'}
                        </span>
                      </td>
                      <td style={{
                        padding: '16px 20px',
                        textAlign: 'right',
                        fontSize: '14px'
                      }}>
                        <div style={{
                          fontWeight: '700',
                          color: activeTab === 'facturas' ? colorVista.solid : '#22c55e'
                        }}>
                          {doc.moneda} ${Number(doc.monto).toLocaleString()}
                        </div>
                      </td>
                      <td style={{
                        padding: '16px 20px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                          <button
                            onClick={() => openDocumentModal(doc)}
                            className="action-button"
                            style={{
                              background: activeTab === 'facturas' ? `${colorVista.solid}20` : '#f0fdf4',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              cursor: 'pointer',
                              color: activeTab === 'facturas' ? colorVista.solid : '#166534',
                              fontSize: '12px',
                              fontWeight: '600',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = activeTab === 'facturas' ? `${colorVista.solid}30` : '#dcfce7';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = activeTab === 'facturas' ? `${colorVista.solid}20` : '#f0fdf4';
                            }}
                          >
                            <Eye size={12} />
                            Ver
                          </button>
                          
                          {doc.documento_url && (
                            <button
                              onClick={() => downloadFile(doc.documento_url, generateFileName(doc))}
                              style={{
                                background: '#f0fdf4',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '8px',
                                cursor: 'pointer',
                                color: '#10b981',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#dcfce7';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = '#f0fdf4';
                              }}
                              title={`Descargar ${generateFileName(doc)}`}
                            >
                              <Download size={12} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ✅ MODAL PARA VER DOCUMENTO CON ESC HANDLER */}
      {showDocumentModal && selectedDocument && (
        <DocumentViewModal
          url={selectedDocument}
          tipo={selectedDocumentType}
          onClose={() => {
            setShowDocumentModal(false);
            setSelectedDocument(null);
            setSelectedDocumentType(null);
          }}
          isMobile={isMobile}
        />
      )}

      {/* ✅ CSS OBLIGATORIO - ANIMACIONES UNIFICADAS */}
      <style jsx>{`
        /* Configuración anti-zoom */
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input, textarea, select, button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        /* Animaciones estándar */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
            max-height: 0;
          }
          to {
            opacity: 1;
            transform: translateY(0);
            max-height: 500px;
          }
        }
        
        .loading-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        /* Hover effects */
        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }
        
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }
        
        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        /* Responsive desktop crítico */
        @media (min-width: 769px) {
          .header-section > div:first-child {
            flex-wrap: nowrap !important;
            min-height: 56px;
          }
          
          .button-hover {
            flex-shrink: 0 !important;
            white-space: nowrap !important;
            min-width: max-content !important;
          }
          
          .main-container {
  min-width: auto;
}
        }
      `}</style>
    </div>
  );
}

export default FacturasComprobantes;